import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import styles from './ui.module.css';

/**
 * Плашка с пояснением: что произойдёт, чего не хватает, что пошло не так.
 *
 * Самый частый примитив персонала — восемьдесят вызовов. Раньше рисовалась
 * четырьмя инлайновыми стилями с цветом засечки из объекта-словаря.
 */

/*
  Тон называет РОЛЬ, а не краску: `pending` и `warning` красятся одинаково
  сиреневым, но значат разное — «ждёт решения» и «осторожно». Цвет в Мезе
  означает состояние заявки, и лиловая полоса у той, что стоит в очереди, —
  это то же самое лиловое, что у её знака.
*/
export type NoticeTone = 'info' | 'pending' | 'warning' | 'error' | 'success';

const BOX: Record<NoticeTone, string> = {
  info: styles.noticeInfo,
  pending: styles.noticeWarning,
  warning: styles.noticeWarning,
  error: styles.noticeError,
  success: styles.noticeSuccess,
};

const TITLE: Record<NoticeTone, string> = {
  info: styles.noticeTitleInfo,
  pending: styles.noticeTitleWarning,
  warning: styles.noticeTitleWarning,
  error: styles.noticeTitleError,
  success: styles.noticeTitleSuccess,
};

export function Notice({
  tone = 'info',
  title,
  icon,
  framed = false,
  children,
  action,
}: {
  tone?: NoticeTone;
  title?: string;
  /** Значок в строке заголовка: треугольник перед необратимым действием. */
  icon?: ReactNode;
  /**
   * Рамка вместо засечки слева. Нужна там, где плашка не поясняет, а
   * ОСТАНАВЛИВАЕТ: засечку читают как примечание на полях и проскакивают.
   */
  framed?: boolean;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={clsx(styles.notice, BOX[tone], framed && styles.noticeFramed)}>
      {title === undefined ? null : (
        <div className={clsx(styles.noticeTitle, TITLE[tone], styles.noticeTitleRow)}>
          {icon}
          {title}
        </div>
      )}
      {children === undefined ? null : <div className={styles.noticeText}>{children}</div>}
      {action === undefined ? null : <div className={styles.noticeAction}>{action}</div>}
    </div>
  );
}

const TAG: Record<NoticeTone, string> = {
  info: styles.tagInfo,
  pending: styles.tagWarning,
  warning: styles.tagWarning,
  error: styles.tagError,
  success: styles.tagSuccess,
};

const TAG_FILL: Record<NoticeTone, string> = {
  info: styles.tagFillInfo,
  pending: styles.tagFillWarning,
  warning: styles.tagFillWarning,
  error: styles.tagFillError,
  success: styles.tagFillSuccess,
};

/**
 * Нейтральная метка: вид персонажа, вид способности, раса.
 *
 * О состоянии вещи не сообщает — для этого StatusBadge. Отличие между ними
 * в СЛОВАРЕ тонов, а не в виде: форму обе берут из одного класса, и метрике
 * разъезжаться больше негде.
 *
 * `size="l"` — чип в шапке экрана, а не внутри карточки. `fill` — залитая
 * плашка: принадлежность, а не свойство, и на экране такая одна.
 */
export function Tag({
  children,
  tone = 'info',
  size = 'm',
  fill = false,
}: {
  children: ReactNode;
  tone?: NoticeTone;
  size?: 'm' | 'l';
  fill?: boolean;
}) {
  return (
    <span
      className={clsx(
        styles.badge,
        fill ? TAG_FILL[tone] : TAG[tone],
        size === 'l' && styles.badgeLarge,
      )}
    >
      {children}
    </span>
  );
}
